import { Flex, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import Button from "../../../Components/Button"
import IntegrationAnimation from "../../../Components/IntegrationAnimation"
import { RoutesPaths } from "../../../constants/user-routes"
import Images from "../../../Images"

import styles from "../styles.module.less"

const { Text } = Typography

const connectVisual = (image) => (
  <img src={image} alt="" width={36} className={styles.chartBannerLogo} />
)

// Keyed by `warning_notification` from `main-info`: the backend names the reason, the page only
// says it. An unknown key renders nothing rather than an empty card.
const WARNINGS = {
  missing_stripe_connection: {
    visual: () => connectVisual(Images.stripe_connect_banner),
    title: "Connect Stripe to see your chargeback rate",
    description:
      "This chart is only available with an active Stripe connection. Connect your Stripe account to see your rate.",
    action: "Connect Stripe now"
  },
  missing_stripe_permissions: {
    visual: () => connectVisual(Images.stripe_connect_banner),
    title: "Your Stripe access needs an update",
    description:
      "Your current Stripe connection doesn’t grant access to the data this chart is built from. Update your Stripe access to continue.",
    action: "Update permissions"
  },
  stripe_report_in_progress: {
    visual: () => (
      <IntegrationAnimation icon={<img src={Images.switcher_stripe} alt="" width={20} />} />
    ),
    title: "Processing your data",
    description:
      "Your Stripe data is being processed. It may take some time — we’ll notify you by email as soon as it’s complete."
  },
  missing_shopify_connection: {
    visual: () => connectVisual(Images.shopify_connect_banner),
    title: "Connect Shopify to see your chargeback rate",
    description:
      "This chart is only available with an active Shopify connection. Connect your Shopify account to see your rate.",
    action: "Connect Shopify now"
  },
  shopify_report_in_progress: {
    visual: () => (
      <IntegrationAnimation icon={<img src={Images.switcher_shopify} alt="" width={20} />} />
    ),
    title: "Processing your data",
    description:
      "Your Shopify data is being processed. It may take some time — we’ll notify you by email as soon as it’s complete."
  }
}

// Covers the chart the way Health's own banner does. Nothing is drawn over: with no processor to
// read, the overview answers with flat zeroes, and this is what says why.
const ChartUnavailableBanner = ({ warning }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const userInfo = useSelector((state) => state.auth.userInfo)

  const content = WARNINGS[warning]
  if (!content) return null

  // Neither a demo account nor one without a business account of its own can connect a processor.
  const canConnect = !userInfo?.is_demo && Boolean(userInfo?.current_business_account_id)

  return (
    <div className={styles.chartBanner} data-testid="home-chart-banner">
      <Flex vertical align="center" gap={8} className={styles.chartBannerContent}>
        {content.visual()}
        <Text strong className={styles.chartBannerTitle}>
          {t(content.title)}
        </Text>
        <Text type="secondary">{t(content.description)}</Text>
        {content.action ? (
          <Button
            type="primary"
            size="large"
            text={t(content.action)}
            disabled={!canConnect}
            onClick={() => navigate(RoutesPaths.configure.processors)}
          />
        ) : null}
      </Flex>
    </div>
  )
}

export default ChartUnavailableBanner
